/* istanbul ignore file */
import React, { useState } from 'react';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Button } from '../../../../components/button';
import { Access, ApplicationArea, PrincipalType, TeamType, WorklistAccessControlPrincipal } from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { TextField } from '../../../../components/text-field/text-field';
import { IAutocompleteValue } from '../../save-dialog';
import { ISavedEntityDefinition } from '../../store/reducers/saved-entity-drawer-reducer';
import { LookupSelector } from '../query-builder/controls/lookup-selector';
import { WorklistAccessControlPrincipalEx } from './saved-entity-form-container';

const useStyles = makeStyles()((theme: Theme) =>
  ({
    list: {
      minWidth: 700,
      width: 700,
      padding: theme.spacing(2),
    },
    container: {
      maxHeight: '24vh',
    },
    cell: {
      maxWidth: '400px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

export interface ISavedEntityDrawerProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  onAddButtonClick: (added: IAutocompleteValue, type: PrincipalType) => void;
  onDeleteClick: (principalId: string) => void;
  onUpdateAccessClick: (principalId: string, access: Access) => void;
  savedEntityDefinitions: ISavedEntityDefinition[];
  applicationAreas: ApplicationArea[];
  hasApplicationAreas: boolean;
  worklistAccessControls: WorklistAccessControlPrincipalEx[];
  onOkButtonClick: () => void;
  isLoading?: boolean;
  area: string;
}

export function SavedEntityFormComponent(props: ISavedEntityDrawerProps) {
  const {
    isDrawerOpen,
    onClose,
    onAddButtonClick,
    onDeleteClick,
    onUpdateAccessClick,
    savedEntityDefinitions,
    applicationAreas,
    worklistAccessControls,
    hasApplicationAreas,
    onOkButtonClick,
    isLoading,
    area,
  } = props;

  const selectedIds = worklistAccessControls
    .filter(x => !x.isDeleted)
    .map(x => x.accessControl?.principalId || '')
    .filter(x => !!x);

  function hasWriteAccess(def: ISavedEntityDefinition) {
    if (!def.accessRole) {
      return false;
    }

    return def.accessRole
      .toLowerCase()
      .split(',')
      .some((x: string) => x.trim() === Access.Write);
  }
  function getReadOnlyWorklistAccessControls() {
    return savedEntityDefinitions.filter(x => !hasWriteAccess(x));
  }

  const readOnlyWorklistAccessControls = getReadOnlyWorklistAccessControls();

  return (
    <Dialog open={isDrawerOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent dividers style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <Box>
          <Box mb={1}>
            <Typography variant="h5">Deling</Typography>
            {savedEntityDefinitions.length > 1 && (
              <Typography sx={{ color: 'rgba(0, 0, 0, 0.75)' }}>Følgende delinger vil blive tilføjet – hvis de allerede eksisterer, vil de blive opdateret.</Typography>
            )}
          </Box>
          {savedEntityDefinitions.length === 1 && (
            <Box p={3}>
              <Box mt={1}>
                <TextField value={savedEntityDefinitions[0]?.name} label="Navn" readOnly />
              </Box>
              <Box mt={1}>
                <TextField value={savedEntityDefinitions[0]?.description} label="Beskrivelse" readOnly />
              </Box>
              {hasApplicationAreas && (
                <Box mt={1}>
                  <TextField label="Vises på" value={applicationAreas?.map(aai => `${aai.name}`).join(', ')} readOnly />
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Divider />
        {readOnlyWorklistAccessControls.length > 0 && (
          <Box mt={3} mb={1}>
            <Typography variant="h3">Du har ikke skriverettigheder til følgende {area}:</Typography>
            <Box>{readOnlyWorklistAccessControls.map(x => x.name).join(', ')}</Box>
          </Box>
        )}
        <Box p={2} pt={0}>
          <Box mt={3} mb={1}>
            <Typography variant="h3">Deling med Teams</Typography>
          </Box>
          <LookupEntity label="Team" universe="citizen" codelist="team" onAddButtonClick={(val, type) => onAddButtonClick(val, type)} selectedIds={selectedIds} />
          <Box pt={2}>
            <AccessControlsByPrincipalType
              worklistAccessControls={worklistAccessControls}
              principalType={PrincipalType.Team}
              onDeleteClick={onDeleteClick}
              onUpdateAccessClick={onUpdateAccessClick}
            />
          </Box>
        </Box>
        <Box p={2} pt={0}>
          <Box mt={3} mb={1}>
            <Typography variant="h3">Deling med Brugere</Typography>
          </Box>
          <LookupEntity label="Bruger" universe="citizen" codelist="systemuser" onAddButtonClick={(val, type) => onAddButtonClick(val, type)} selectedIds={selectedIds} />
          <Box pt={2}>
            <AccessControlsByPrincipalType
              worklistAccessControls={worklistAccessControls}
              principalType={PrincipalType.User}
              onDeleteClick={onDeleteClick}
              onUpdateAccessClick={onUpdateAccessClick}
            />
          </Box>
        </Box>
        <DialogActions>
          <Button variant="contained" onClick={onOkButtonClick} color="primary" loading={isLoading}>
            Udfør
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Afbryd
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

interface IAccessControlByPrincipleTypeProps {
  worklistAccessControls: WorklistAccessControlPrincipalEx[];
  principalType: PrincipalType;
  onDeleteClick: (principalId: string) => void;
  onUpdateAccessClick: (principalId: string, access: Access) => void;
}

function AccessControlsByPrincipalType(props: IAccessControlByPrincipleTypeProps) {
  const { classes } = useStyles();

  const { worklistAccessControls, principalType, onDeleteClick, onUpdateAccessClick } = props;
  const items = worklistAccessControls
    .filter(o => o.accessControl?.principalType === principalType && !o.isDeleted)
    .sort((first, second) => ((first.pricipalName || '') > (second.pricipalName || '') ? 1 : -1));
  if (items.length === 0) {
    return null;
  }

  const pricipalTypeText = principalType === PrincipalType.User ? 'Bruger' : 'Team';

  return (
    <Paper>
      <TableContainer className={classes.container}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width="400">{pricipalTypeText}</TableCell>
              <TableCell align="center">Del ejerskab</TableCell>
              <TableCell align="center">Fjern</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row: WorklistAccessControlPrincipal) => {
              const { principalId } = row.accessControl || {};
              return (
                <TableRow key={principalId}>
                  <TableCell className={classes.cell} width="400" title={row.tooltipDescription}>
                    {row.pricipalName}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      style={{ margin: 0, padding: 0 }}
                      checked={row.accessControl?.access?.toLowerCase().includes(Access.Write)}
                      onClick={() => principalId && onUpdateAccessClick(principalId, row.accessControl?.access.toLowerCase().includes(Access.Write) ? Access.Read : Access.Write)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FontAwesomeIcon color="#d51a65" icon={faTimes} onClick={() => principalId && onDeleteClick(principalId)} style={{ cursor: 'pointer', fontSize: '24px' }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

interface ISharingTableRowProps {
  row: WorklistAccessControlPrincipal;
  onUpdateAccess: (principalId: string, access: Access) => void;
  onDeleteClick: (principalId: string) => void;
  classes: Record<'cell', string>;
}

function SharingTableRow(props: ISharingTableRowProps) {
  const { row, onUpdateAccess, onDeleteClick, classes } = props;
  const principalId = row.accessControl?.principalId;
  return (
    <TableRow key={principalId}>
      <TableCell className={classes.cell} width="400" title={row.tooltipDescription}>
        {row.pricipalName}
      </TableCell>
      <TableCell align="center">
        <Checkbox
          style={{ margin: 0, padding: 0 }}
          checked={row.accessControl?.access?.toLowerCase().includes(Access.Write)}
          onClick={() => principalId && onUpdateAccess(principalId, row.accessControl?.access.toLowerCase().includes(Access.Write) ? Access.Read : Access.Write)}
        />
      </TableCell>
      <TableCell align="center">
        <FontAwesomeIcon color="#d51a65" icon={faTimes} onClick={() => principalId && onDeleteClick(principalId)} style={{ cursor: 'pointer', fontSize: '24px' }} />
      </TableCell>
    </TableRow>
  );
}

interface ILookupEntityProps {
  universe: string;
  codelist: string;
  onAddButtonClick: (users: IAutocompleteValue, type: PrincipalType) => void;
  label: string;
  selectedIds: string[];
}

function LookupEntity(props: ILookupEntityProps) {
  const { onAddButtonClick, universe, codelist, label, selectedIds } = props;

  const multiSelectEntityLookupClient = moduleClientFactory.createMultiSelectEntityLookupClient();

  const [isLoading, setIsLoading] = useState(false);
  const [showNoOptionMessage, setShowNoOptionMessage] = useState(false);

  const [data, setData] = useState<IAutocompleteValue[]>([]);

  async function handleSearch(text: string) {
    setShowNoOptionMessage(true);
    setIsLoading(true);

    const result = await multiSelectEntityLookupClient.getResultByLookupEntityWithTeamTypeActiveOnly(universe, codelist, text, 50, TeamType.Organisatoriskenhed);
    const dataResult = result
      .filter(x => !selectedIds.some(y => y === x.value))
      .map(r => ({ title: r.label, value: r.value, object: r.shortLabel?.replace('–', '-') } as IAutocompleteValue));

    setData(dataResult);
    setIsLoading(false);
  }

  function handleAutoCompleteChange(value: IAutocompleteValue | IAutocompleteValue[]) {
    onAddButtonClick(value as IAutocompleteValue, codelist === 'systemuser' ? PrincipalType.User : PrincipalType.Team);
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Box flexGrow={2}>
        <LookupSelector
          noOptionsMessage={showNoOptionMessage ? 'Ingen resultater' : ''}
          isLoading={isLoading}
          onSearch={handleSearch}
          onSelect={handleAutoCompleteChange}
          value={[]}
          data={data}
          textFieldProps={{ label }}
          isMulti={false}
        />
      </Box>
      {/* <Box p={3} flexGrow={0}>
        <FontAwesomeIcon icon={faPlus} onClick={handleClick} style={{ cursor: 'pointer', fontSize: '24px' }} />
      </Box> */}
    </Box>
  );
}
